import { ReactNode } from "react";
import { ThirdPartyDeviceType } from "verdi-common";
import { DeviceConfiguration } from "verdi-common/DeviceConfigs";
import type { ConnectedEquipment } from "verdi-common/schemas/device/device";
import DraginoGatewayIcon from "../../../../../icons/inline-react-svgs/add-device/dragino-gateway-icon";
import PumpIcon from "../../../../../icons/svgs/assets/pump.svg?react";
import ValveIcon from "../../../../../icons/svgs/assets/valve.svg?react";

import { isValve } from "../../../../../services/device-attributes/attributes/general";
import { isThirdPartyGateway } from "../../../../../services/device-attributes/configurations";
import { ICON_VARIANT } from "../../configs/variants";
import SensorIcon from "./assets/sensor.svg?react";
import { TextIcon } from "./text-icon";

/**
 * Scaling factor for the icon graphic (instead of directly mapping to each of the icon sizes)
 */
export const markerIconSizeScaling: Record<ICON_VARIANT, number> = {
    [ICON_VARIANT.GROUP]: 0.5,
    [ICON_VARIANT.MANUAL_MODE]: 0.5,
    [ICON_VARIANT.SENSOR]: 0.75,
    [ICON_VARIANT.VALVE]: 0.6,
    [ICON_VARIANT.DRAGINO_GATEWAY]: 0.75,
} as const;

/**
 * default color(s) for the icon graphic
 * gets overridden by the deviceState.colorParams.iconColor prop if its set
 */
export const iconColors = {
    default: "#424242",
} as const;

/**
 * Lookup for the icon graphic props
 */
export type IconConfig = {
    icon: React.ElementType;
    type: "SVG" | "Text";
    color?: string;
    centerContent?: ReactNode; // Manually override center
};
const iconConfigs: Record<ICON_VARIANT, IconConfig> = {
    [ICON_VARIANT.SENSOR]: {
        icon: SensorIcon,
        type: "SVG",
    },
    [ICON_VARIANT.VALVE]: {
        icon: ValveIcon,
        type: "SVG",
    },
    [ICON_VARIANT.GROUP]: {
        icon: TextIcon,
        type: "Text",
    },
    [ICON_VARIANT.MANUAL_MODE]: {
        icon: TextIcon,
        type: "Text",
        centerContent: "M",
    },
    [ICON_VARIANT.DRAGINO_GATEWAY]: {
        icon: DraginoGatewayIcon,
        type: "SVG",
    },
};

/**
 * Logic for selecting the appropriate icon graphic props based on the device state
 */
type MakeIconConfigProps = {
    manualMode?: boolean;
    deviceType: DeviceConfiguration["type"] | "GROUP" | ThirdPartyDeviceType;
    connectedEquipment?: ConnectedEquipment;
};
export const makeIconConfig = ({
    manualMode,
    deviceType,
    connectedEquipment,
}: MakeIconConfigProps): { variant: ICON_VARIANT; iconConfig: IconConfig } => {
    // Slightly awkward to run this through the function instead of just looking up props directly from the object,
    // but here added to unify prop lookup for all icon graphics
    if (deviceType === "GROUP") {
        return {
            variant: ICON_VARIANT.GROUP,
            iconConfig: iconConfigs[ICON_VARIANT.GROUP],
        };
    }

    if (manualMode) {
        return {
            variant: ICON_VARIANT.MANUAL_MODE,
            iconConfig: iconConfigs[ICON_VARIANT.MANUAL_MODE],
        };
    }

    if (isThirdPartyGateway(deviceType)) {
        return {
            variant: ICON_VARIANT.DRAGINO_GATEWAY,
            iconConfig: iconConfigs[ICON_VARIANT.DRAGINO_GATEWAY],
        };
    }

    if (isValve({ deviceType })) {
        return {
            variant: ICON_VARIANT.VALVE,
            iconConfig: {
                ...iconConfigs[ICON_VARIANT.VALVE],
                icon: connectedEquipment === "pump" ? PumpIcon : ValveIcon,
            },
        };
    }
    return {
        variant: ICON_VARIANT.SENSOR,
        iconConfig: iconConfigs[ICON_VARIANT.SENSOR],
    };
};
